// Horse Profile — Today / Timeline / Trends tabs.

import { useState, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import HydrationDetail from "../components/HydrationDetail";
import { AVATAR_TONES } from "../lib/avatarTones";
import type { HorseColour } from "../lib/avatarTones";
import {
  computePace,
  paceFraction,
  paceLabel,
  statusLabel,
  fmtAgo,
} from "../lib/hydration";
import type { PaceStatus } from "../lib/hydration";
import { useIsMobile } from "../lib/useMediaQuery";
import { useCurrentUser } from "../lib/useCurrentUser";
import { useUserYard } from "../lib/yard";
import { useHorse, useHorseLogs, todayId } from "../lib/dailyLogs";
import type { Horse, DailyLog as DailyLogDoc } from "../lib/types";
import { MOCK_HORSES, MOCK_YARD, MOCK_NOW_HOUR, MOCK_HORSE_HISTORIES } from "../lib/mockData";
import type { MockHorse, MockHistoryDay } from "../lib/mockData";
import "../styles/dashboard.css";
import "../styles/horse-profile.css";

// --- Helpers to bridge real Firestore data into the MockHorse / MockHistoryDay
// shapes that the existing render code reads. Stays inline rather than shared
// because Dashboard / DailyLog / HydrationResult each need slightly different
// `today` handling.

function capitalize(s: string | null | undefined): string {
  if (!s) return "Normal";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function dropTagToLabel(tag: string | null | undefined): string {
  if (!tag) return "Normal";
  if (tag === "normal") return "Normal";
  if (tag === "soft-loose" || tag === "soft") return "Soft / loose";
  if (tag === "hard-dry" || tag === "hard") return "Hard / dry";
  if (tag === "none-seen" || tag === "none") return "None seen";
  return capitalize(tag);
}

function realHorseToMock(horse: Horse, todayLog: DailyLogDoc | null | undefined): MockHorse {
  const validColour =
    horse.colour && (horse.colour in AVATAR_TONES) ? (horse.colour as HorseColour) : "bay";
  const targetL = todayLog?.ehe?.targetL ?? Math.max(20, Math.round(horse.weightKg * 0.08));
  let lastLoggedMin: number | null = null;
  if (todayLog?.updatedAt) {
    const updated = todayLog.updatedAt.toMillis ? todayLog.updatedAt.toMillis() : 0;
    if (updated) lastLoggedMin = Math.max(0, Math.floor((Date.now() - updated) / 60000));
  }
  return {
    id: horse.id,
    name: horse.name,
    colour: validColour,
    breed: horse.breed ?? "—",
    age: horse.age ?? 0,
    weightKg: horse.weightKg,
    waterL: todayLog?.water ?? 0,
    targetL,
    lastLoggedMin,
    flags: {},
    history5: [],
    today: todayLog
      ? {
          exercise: {
            mins: todayLog.exercise?.mins ?? 0,
            intensity: todayLog.exercise?.intensity ?? null,
          },
          feed: (todayLog.feed ?? []).map((f) => ({ type: f.type, kg: f.kg })),
          behaviour: capitalize(typeof todayLog.behaviour === "string" ? todayLog.behaviour : null),
          droppings: dropTagToLabel(todayLog.droppings?.tag),
          droppingsCount: todayLog.droppings?.count ?? 0,
          notes: todayLog.notes ?? "",
        }
      : null,
  };
}

function logsToHistory(logs: DailyLogDoc[], defaultTargetL: number): MockHistoryDay[] {
  // useHorseLogs returns newest-first; flip to oldest-first to match the
  // mock convention (today is the last entry).
  const sorted = [...logs].reverse();
  const today = todayId();
  return sorted.map((log) => {
    const date = new Date(`${log.id}T00:00:00`);
    const isToday = log.id === today;
    const rawStatus = log.ehe?.statusKey;
    const status: PaceStatus =
      rawStatus && rawStatus !== "idle" ? (rawStatus as PaceStatus) : "hydrated";
    return {
      date,
      dateLabel: date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
      dayShort: date.toLocaleDateString("en-GB", { weekday: "narrow" }),
      intakeL: log.water ?? 0,
      targetL: log.ehe?.targetL ?? defaultTargetL,
      status,
      behaviour: typeof log.behaviour === "string" ? log.behaviour : "normal",
      droppings: log.droppings?.tag ?? "normal",
      exerciseMins: log.exercise?.mins ?? 0,
      exerciseIntensity: log.exercise?.intensity ?? "",
      isToday,
      partial: isToday,
    };
  });
}

type TabKey = "today" | "timeline" | "trends";

const PIc = {
  back: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  edit: <svg width="13" height="13" viewBox="0 0 14 14" fill="none"><path d="M2.5 11.5L11.5 2.5L13 4L4 13L2 13L2.5 11.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" /></svg>,
  plus: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>,
};

// ---------- Header (shared across tabs) ----------------------------------
interface ProfileHeaderProps {
  horse: MockHorse;
  status: PaceStatus;
  expected: number;
  isMobile: boolean;
}

function ProfileHeader({ horse, status, expected, isMobile }: ProfileHeaderProps) {
  const navigate = useNavigate();
  const tone = AVATAR_TONES[horse.colour] || "#5B6470";
  const pace = computePace(horse.waterL, horse.targetL, MOCK_NOW_HOUR);
  const pacePct = Math.min(100, (horse.waterL / horse.targetL) * 100);
  const tickPct = Math.min(100, (expected / horse.targetL) * 100);
  const label = paceLabel(horse.waterL, expected);

  return (
    <header className="hp-header">
      <button className="link-btn hp-back" onClick={() => navigate("/dashboard")}>
        {PIc.back} Back to dashboard
      </button>
      <div className="hp-id-row">
        <span
          className="avatar"
          style={{
            background: tone,
            width: isMobile ? 56 : 64,
            height: isMobile ? 56 : 64,
            fontSize: isMobile ? 22 : 26,
          }}
        >
          {horse.name[0]}
        </span>
        <div className="hp-id-text">
          <h1 className="hp-name">{horse.name}</h1>
          <p className="hp-meta">{horse.breed} · {horse.age}y · {horse.weightKg}kg · {horse.colour}</p>
        </div>
        <span className={`badge badge-lg ${status}`}>
          <span className={`dot ${status}`}></span>
          {statusLabel(status)}
        </span>
      </div>
      <div className="hp-snapshot">
        <div className="hp-snap-row">
          <div className="hp-snap-amount mono">
            <span className="hp-snap-l">{horse.waterL}</span>
            <span className="hp-snap-unit">L</span>
            <span className="faint hp-snap-of">drunk so far · target {horse.targetL}L</span>
          </div>
          <span className={`pace-pill pace-tone-${label.tone}`}>{label.text}</span>
        </div>
        <div className="pacebar">
          <div className={`bar ${pace.status}`}>
            <span style={{ width: `${pacePct}%` }}></span>
            {expected > 0.5 && <span className="pace-tick" style={{ left: `${tickPct}%` }}></span>}
          </div>
        </div>
        <div className="hp-snap-meta mono">
          <span className="faint">expected </span>{Math.round(expected)}L
          <span className="faint"> · last logged </span>{fmtAgo(horse.lastLoggedMin)}
        </div>
      </div>
      <div className="hp-actions">
        <button className="btn primary lg hp-log-btn" onClick={() => navigate(`/horses/${horse.id}/log`)}>
          {PIc.plus} Log today
        </button>
        <button className="btn ghost" onClick={() => navigate(`/horses/${horse.id}/edit`)}>
          {PIc.edit} Edit horse
        </button>
      </div>
    </header>
  );
}

// ---------- Tabs --------------------------------------------------------
interface TabsProps {
  value: TabKey;
  onChange: (v: TabKey) => void;
}

function Tabs({ value, onChange }: TabsProps) {
  const opts: { k: TabKey; label: string }[] = [
    { k: "today", label: "Today" },
    { k: "timeline", label: "Timeline" },
    { k: "trends", label: "Trends" },
  ];
  return (
    <div className="hp-tabs" role="tablist">
      {opts.map((o) => (
        <button
          key={o.k}
          role="tab"
          aria-selected={value === o.k}
          className={`hp-tab ${value === o.k ? "active" : ""}`}
          onClick={() => onChange(o.k)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------- Today tab ---------------------------------------------------
// Combines the EHE result + recommendation + "show the maths" dropdown
// (from the old standalone HydrationResult page) with the original
// today's-log detail card. The "Show maths" dropdown defaults open when
// the user just submitted a log (URL ?logged=1), closed otherwise.
interface TodayTabProps {
  horse: MockHorse;
  mathsOpenDefault?: boolean;
  navigate: ReturnType<typeof useNavigate>;
  horseId: string;
}
function TodayTab({ horse, mathsOpenDefault = false, navigate, horseId }: TodayTabProps) {
  const today = horse.today;
  if (!today) {
    return (
      <div className="hp-empty">
        <h2>Not logged today yet</h2>
        <p>Tap "Log today" above to start. Sections prefill from baseline.</p>
      </div>
    );
  }

  const pace = computePace(horse.waterL, horse.targetL, MOCK_NOW_HOUR);

  return (
    <>
      <HydrationDetail
        horse={horse}
        mathsOpenDefault={mathsOpenDefault}
        statusOverride={pace.status}
      />

      <article
        className="hp-card hp-log-card"
        style={{ marginTop: 18 }}
      >
        <header className="hp-card-head">
          <h2 className="hp-card-title">Today's log</h2>
          <button
            className="link-btn"
            onClick={() => navigate(`/horses/${horseId}/log`)}
          >
            {PIc.edit} Edit log
          </button>
        </header>
        <dl className="hp-log-dl">
          <dt>Water</dt>
          <dd className="mono">{horse.waterL}L <span className="faint">/ {horse.targetL}L target</span></dd>

          <dt>Exercise</dt>
          <dd>
            {today.exercise.mins > 0
              ? `${today.exercise.mins} min, ${(today.exercise.intensity ?? "").toLowerCase()}`
              : <span className="faint">None</span>}
          </dd>

          <dt>Feed</dt>
          <dd>{today.feed.map((f) => `${f.kg}kg ${f.type}`).join(" · ")}</dd>

          <dt>Behaviour</dt>
          <dd><span className="dot hydrated"></span> {today.behaviour}</dd>

          <dt>Droppings</dt>
          <dd>
            <span className="dot hydrated"></span> {today.droppings}
            {today.droppingsCount != null && <span className="faint"> · {today.droppingsCount} piles</span>}
          </dd>

          <dt>Notes</dt>
          <dd className="hp-log-notes">{today.notes || <span className="faint">None</span>}</dd>

          <dt>Conditions</dt>
          <dd className="mono">{MOCK_YARD.weatherC}°C <span className="faint">from OpenWeather</span></dd>
        </dl>
      </article>
    </>
  );
}

// ---------- Timeline tab ------------------------------------------------
function TimelineTab({ days }: { horse: MockHorse; days: MockHistoryDay[] }) {
  const [filter, setFilter] = useState<"all" | "concerns" | "week" | "month">("all");
  const [expanded, setExpanded] = useState<number | null>(null);
  const ordered = useMemo(() => [...days].reverse(), [days]);

  const filtered = useMemo(() => {
    if (filter === "concerns") return ordered.filter((d) => ["mild", "moderate", "severe", "critical"].includes(d.status));
    if (filter === "week") return ordered.slice(0, 7);
    if (filter === "month") return ordered.slice(0, 30);
    return ordered;
  }, [ordered, filter]);

  const concernsCount = ordered.filter((d) => ["mild", "moderate", "severe", "critical"].includes(d.status)).length;

  if (days.length === 0) {
    return (
      <div className="hp-empty">
        <h2>No logs yet</h2>
        <p>
          Save your first daily log and it'll show up here. Come back tomorrow
          to see your timeline build out.
        </p>
      </div>
    );
  }

  const filters: Array<{ k: "all" | "concerns" | "week" | "month"; label: string; count?: number }> = [
    { k: "all", label: "All" },
    { k: "concerns", label: "Concerns only", count: concernsCount },
    { k: "week", label: "This week" },
    { k: "month", label: "This month" },
  ];

  return (
    <div className="hp-timeline">
      <div className="hp-tl-filters">
        {filters.map((f) => (
          <button
            key={f.k}
            className={`chip ${filter === f.k ? "active" : ""}`}
            onClick={() => setFilter(f.k)}
          >
            {f.label}
            {f.count != null && <span className="count">{f.count}</span>}
          </button>
        ))}
      </div>

      <div className="hp-tl-list">
        {filtered.map((d, i) => {
          const isExp = expanded === i;
          const dropTone =
            d.droppings === "normal" ? "hydrated"
            : d.droppings === "none-seen" ? "severe"
            : "mild";
          const behavTone =
            ["bright", "normal"].includes(d.behaviour) ? "hydrated"
            : ["restless", "distressed"].includes(d.behaviour) ? "severe"
            : "mild";
          return (
            <div
              key={i}
              className={`hp-tl-row ${isExp ? "is-exp" : ""} ${d.isToday ? "is-today" : ""}`}
            >
              <button className="hp-tl-row-head" onClick={() => setExpanded(isExp ? null : i)}>
                <span className="hp-tl-date">
                  {d.isToday ? <strong>Today</strong> : d.dateLabel}
                </span>
                <span className={`hp-tl-status badge ${d.status}`}>
                  <span className={`dot ${d.status}`}></span>{statusLabel(d.status)}
                </span>
                <span className="hp-tl-water mono">
                  {d.intakeL}L
                  {!d.isToday && <span className="faint"> /{d.targetL}</span>}
                  {d.isToday && <span className="faint"> so far</span>}
                </span>
                <span className="hp-tl-behaviour">
                  <span className={`dot ${behavTone}`}></span>{d.behaviour}
                </span>
                <span className="hp-tl-droppings">
                  <span className={`dot ${dropTone}`}></span>{d.droppings.replace("-", " / ")}
                </span>
                <span className="hp-tl-chev">{isExp ? "−" : "+"}</span>
              </button>
              {isExp && (
                <div className="hp-tl-row-body">
                  <dl className="hp-tl-dl">
                    <dt>Water</dt><dd className="mono">{d.intakeL}L / {d.targetL}L</dd>
                    <dt>Exercise</dt>
                    <dd>{d.exerciseMins > 0 ? `${d.exerciseMins} min, ${d.exerciseIntensity}` : "None"}</dd>
                    <dt>Behaviour</dt>
                    <dd><span className={`dot ${behavTone}`}></span>{d.behaviour}</dd>
                    <dt>Droppings</dt>
                    <dd><span className={`dot ${dropTone}`}></span>{d.droppings.replace("-", " / ")}</dd>
                  </dl>
                  <button className="link-btn">{PIc.edit} View / edit full log</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Trends tab --------------------------------------------------
function TrendsTab({ horse, days }: { horse: MockHorse; days: MockHistoryDay[] }) {
  if (days.length < 2) {
    return (
      <div className="hp-empty">
        <h2>Come back tomorrow</h2>
        <p>
          Trends compare your daily logs over time. You need at least two
          days of data to see the first chart. Log {horse.name} today, then
          come back tomorrow for your first trend.
        </p>
      </div>
    );
  }
  const maxIntake = Math.max(horse.targetL, ...days.map((d) => d.intakeL)) * 1.05;
  const avgIntake = (
    days.slice(0, -1).reduce((a, d) => a + d.intakeL, 0) / Math.max(1, days.length - 1)
  ).toFixed(1);
  const hydratedDays = days.filter((d) => d.status === "hydrated").length;
  const maxExer = Math.max(60, ...days.map((d) => d.exerciseMins));

  return (
    <div className="hp-trends">
      <article className="hp-card hp-chart-card">
        <header className="hp-card-head">
          <div>
            <h2 className="hp-card-title">Hydration</h2>
            <p className="hp-card-sub muted">Last 14 days · vs daily target</p>
          </div>
          <div className="hp-chart-stats">
            <span><strong className="mono">{avgIntake}L</strong> <span className="faint">avg</span></span>
            <span><strong className="mono">{horse.targetL}L</strong> <span className="faint">target</span></span>
            <span><strong className="mono">{hydratedDays}/{days.length}</strong> <span className="faint">hydrated</span></span>
          </div>
        </header>
        <HydrationChart days={days} maxIntake={maxIntake} targetL={horse.targetL} />
        <div className="hp-chart-legend">
          <span><span className="dot hydrated"></span>Hydrated</span>
          <span><span className="dot mild"></span>Mild</span>
          <span><span className="dot moderate"></span>Moderate</span>
          <span><span className="dot severe"></span>Severe</span>
          <span className="hp-leg-divider">|</span>
          <span><span className="hp-target-marker"></span>Target line</span>
          <span><span className="hp-partial-marker"></span>Today (partial)</span>
        </div>
      </article>

      <article className="hp-card hp-chart-card">
        <header className="hp-card-head">
          <div>
            <h2 className="hp-card-title">Exercise</h2>
            <p className="hp-card-sub muted">Last 14 days · minutes worked</p>
          </div>
          <div className="hp-chart-legend">
            <span><span className="exer-dot light"></span>Light</span>
            <span><span className="exer-dot medium"></span>Medium</span>
            <span><span className="exer-dot intense"></span>Intense</span>
          </div>
        </header>
        <ExerciseChart days={days} maxMins={maxExer} />
      </article>

      <article className="hp-card hp-chart-card">
        <header className="hp-card-head">
          <div>
            <h2 className="hp-card-title">Behaviour</h2>
            <p className="hp-card-sub muted">One tag per day</p>
          </div>
        </header>
        <StripChart
          days={days}
          getTone={(d) =>
            ["bright", "normal"].includes(d.behaviour) ? "hydrated"
            : ["restless", "distressed"].includes(d.behaviour) ? "severe"
            : "mild"
          }
          getLabel={(d) => d.behaviour}
        />
      </article>

      <article className="hp-card hp-chart-card">
        <header className="hp-card-head">
          <div>
            <h2 className="hp-card-title">Droppings</h2>
            <p className="hp-card-sub muted">Early colic indicator</p>
          </div>
        </header>
        <StripChart
          days={days}
          getTone={(d) =>
            d.droppings === "normal" ? "hydrated"
            : d.droppings === "none-seen" ? "severe"
            : "mild"
          }
          getLabel={(d) => d.droppings.replace("-", " / ")}
        />
      </article>
    </div>
  );
}

// ---------- Charts ------------------------------------------------------
function HydrationChart({ days, maxIntake, targetL }: { days: MockHistoryDay[]; maxIntake: number; targetL: number }) {
  const W = 600, H = 180;
  const padL = 26, padR = 12, padT = 14, padB = 22;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const bw = innerW / Math.max(1, days.length);
  const targetY = padT + innerH - (targetL / maxIntake) * innerH;
  return (
    <div className="hp-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="hp-chart" preserveAspectRatio="none">
        {[0.25, 0.5, 0.75].map((g) => {
          const y = padT + innerH - g * innerH;
          return (
            <line
              key={g}
              x1={padL}
              x2={W - padR}
              y1={y}
              y2={y}
              stroke="var(--line)"
              strokeWidth="1"
              strokeDasharray="2 4"
            />
          );
        })}
        <line
          x1={padL}
          x2={W - padR}
          y1={targetY}
          y2={targetY}
          stroke="var(--ink-2)"
          strokeWidth="1.4"
          strokeDasharray="4 3"
        />
        <text x={W - padR - 4} y={targetY - 4} textAnchor="end" className="hp-axis">
          {targetL}L target
        </text>

        {days.map((d, i) => {
          const x = padL + i * bw + 3;
          const w = Math.max(8, bw - 6);
          const h = (d.intakeL / maxIntake) * innerH;
          const y = padT + innerH - h;
          const fill = `var(--status-${d.status})`;
          return (
            <g key={i}>
              <rect
                x={x}
                y={y}
                width={w}
                height={Math.max(2, h)}
                rx={2}
                fill={fill}
                opacity={d.isToday ? 0.55 : 1}
                stroke={d.isToday ? "var(--ink)" : "none"}
                strokeWidth={d.isToday ? 1 : 0}
                strokeDasharray={d.isToday ? "3 2" : ""}
              >
                <title>{d.dateLabel}: {d.intakeL}L</title>
              </rect>
              <text x={x + w / 2} y={H - padB + 14} textAnchor="middle" className="hp-axis">
                {d.dayShort}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ExerciseChart({ days, maxMins }: { days: MockHistoryDay[]; maxMins: number }) {
  const W = 600, H = 140;
  const padL = 26, padR = 12, padT = 8, padB = 22;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;
  const bw = innerW / Math.max(1, days.length);
  return (
    <div className="hp-chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="hp-chart" preserveAspectRatio="none">
        {[0.5].map((g) => {
          const y = padT + innerH - g * innerH;
          return (
            <line
              key={g}
              x1={padL}
              x2={W - padR}
              y1={y}
              y2={y}
              stroke="var(--line)"
              strokeWidth="1"
              strokeDasharray="2 4"
            />
          );
        })}
        {days.map((d, i) => {
          const x = padL + i * bw + 3;
          const w = Math.max(8, bw - 6);
          const h = d.exerciseMins > 0 ? (d.exerciseMins / maxMins) * innerH : 0;
          const y = padT + innerH - h;
          const fill =
            d.exerciseIntensity === "light" ? "#9CB6D9"
            : d.exerciseIntensity === "medium" ? "#3F7FB8"
            : d.exerciseIntensity === "intense" ? "#D9651A"
            : "var(--line)";
          return (
            <g key={i}>
              {h > 0 && (
                <rect x={x} y={y} width={w} height={h} rx={2} fill={fill}>
                  <title>{d.dateLabel}: {d.exerciseMins} min {d.exerciseIntensity}</title>
                </rect>
              )}
              {h === 0 && (
                <rect x={x} y={padT + innerH - 4} width={w} height={2} rx={1} fill="var(--ink-5)">
                  <title>{d.dateLabel}: rest day</title>
                </rect>
              )}
              <text x={x + w / 2} y={H - padB + 14} textAnchor="middle" className="hp-axis">
                {d.dayShort}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

interface StripChartProps {
  days: MockHistoryDay[];
  getTone: (d: MockHistoryDay) => string;
  getLabel: (d: MockHistoryDay) => string;
}

function StripChart({ days, getTone, getLabel }: StripChartProps) {
  return (
    <div className="hp-strip">
      {days.map((d, i) => {
        const tone = getTone(d);
        return (
          <div key={i} className="hp-strip-cell" title={`${d.dateLabel}: ${getLabel(d)}`}>
            <span className={`hp-strip-dot dot ${tone}`}></span>
            <span className="hp-strip-day">{d.dayShort}</span>
          </div>
        );
      })}
    </div>
  );
}

// ---------- Main --------------------------------------------------------
export default function HorseProfile() {
  const { horseId } = useParams<{ horseId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // ?logged=1 signals the user just submitted a daily log — opens the
  // "show maths" dropdown by default. Otherwise (just clicked View) it's
  // collapsed.
  const justLogged = searchParams.get("logged") === "1";
  const isMobile = useIsMobile();
  const { user, isDemo } = useCurrentUser();
  const { yard, loading: yardLoading } = useUserYard(user?.uid);
  const { horse: realHorse, loading: realHorseLoading } = useHorse(yard?.id, horseId);
  const { logs: realLogs, loading: logsLoading } = useHorseLogs(yard?.id, horseId, 14);
  const [tab, setTab] = useState<TabKey>("today");

  const todayLog = useMemo<DailyLogDoc | null>(() => {
    if (!realLogs.length) return null;
    const today = todayId();
    return realLogs.find((l) => l.id === today) ?? null;
  }, [realLogs]);

  const horse = useMemo<MockHorse | null>(() => {
    if (isDemo) return MOCK_HORSES.find((h) => h.id === horseId) ?? null;
    if (!realHorse) return null;
    return realHorseToMock(realHorse, todayLog);
  }, [isDemo, horseId, realHorse, todayLog]);

  const days = useMemo<MockHistoryDay[]>(() => {
    if (isDemo && horse) return MOCK_HORSE_HISTORIES[horse.id] ?? [];
    if (!horse) return [];
    return logsToHistory(realLogs, horse.targetL);
  }, [isDemo, horse, realLogs]);

  // Loading window — only for non-demo while Firestore hydrates.
  if (!isDemo && (yardLoading || realHorseLoading || logsLoading)) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--bg)", color: "var(--ink-3)", fontSize: 14 }}>
        Loading…
      </div>
    );
  }

  if (!horse) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--bg)" }}>
        <div style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: "var(--r-lg)", padding: "40px 32px", textAlign: "center", maxWidth: 420 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, margin: "0 0 8px", letterSpacing: "-0.02em" }}>Horse not found</h1>
          <p style={{ color: "var(--ink-3)", fontSize: 14, margin: "0 0 20px", lineHeight: 1.5 }}>
            This horse isn't on your yard.
          </p>
          <button className="btn ghost" onClick={() => navigate("/dashboard")}>← Back to dashboard</button>
        </div>
      </div>
    );
  }

  const pace = computePace(horse.waterL, horse.targetL, MOCK_NOW_HOUR);
  const expected = horse.targetL * paceFraction(MOCK_NOW_HOUR);
  const yardName = isDemo ? MOCK_YARD.name : yard?.name ?? "";

  return (
    <div className={`hp ${isMobile ? "is-mobile" : "is-desktop"}`}>
      <div className="topbar dlog-topbar">
        <div className="topbar-inner">
          <button className="icon-btn dlog-back" aria-label="Back" onClick={() => navigate("/dashboard")}>
            {PIc.back}
          </button>
          <div className="dlog-tb-id">
            <span className="dlog-tb-name">{horse.name}</span>
            <span className="dlog-tb-sub muted">· {yardName}</span>
          </div>
          <button className="icon-btn" aria-label="More">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="4" cy="8" r="1.3" fill="currentColor" />
              <circle cx="8" cy="8" r="1.3" fill="currentColor" />
              <circle cx="12" cy="8" r="1.3" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>

      <main className="hp-page">
        <ProfileHeader horse={horse} status={pace.status} expected={expected} isMobile={isMobile} />
        <div className="hp-tabs-wrap">
          <Tabs value={tab} onChange={setTab} />
        </div>
        <div className="hp-tab-body">
          {tab === "today" && (
            <TodayTab
              horse={horse}
              mathsOpenDefault={justLogged}
              navigate={navigate}
              horseId={horse.id}
            />
          )}
          {tab === "timeline" && <TimelineTab horse={horse} days={days} />}
          {tab === "trends" && <TrendsTab horse={horse} days={days} />}
        </div>

        <p
          style={{
            maxWidth: 760,
            margin: "32px auto 0",
            padding: "14px 16px",
            background: "var(--surface-sunk)",
            border: "1px solid var(--line)",
            borderRadius: "var(--r-md)",
            fontSize: 12,
            color: "var(--ink-3)",
            lineHeight: 1.5,
          }}
        >
          The EHE result is a research-backed estimate, not a vet diagnosis.
          The calculation is a guide and may not reflect individual horse
          needs. If you're worried about a horse, call your vet.
        </p>
      </main>
    </div>
  );
}
